'use client';

import { useJotaiCart } from '@/contexts/JotaiCartProvider';
import { getCartUrl } from '@/utils/cart-url';
import { cn } from '@/utils/cn';
import { ShoppingCart } from 'lucide-react';
import { useLocale } from 'next-intl';
import Link from 'next/link';
import { useEffect, useState } from 'react';

export default function FloatingCartIcon() {
    const { items, isInitialized } = useJotaiCart();
    const locale = useLocale();
    const [isAnimating, setIsAnimating] = useState(false);
    const [previousItemCount, setPreviousItemCount] = useState(0);

    // Calculate total quantity
    const totalQuantity = items.reduce((sum, item) => sum + item.qty, 0);

    // Trigger animation when items are added
    useEffect(() => {
        if (totalQuantity > previousItemCount && previousItemCount > 0) {
            setIsAnimating(true);
            const timer = setTimeout(() => setIsAnimating(false), 600);
            return () => clearTimeout(timer);
        }
        setPreviousItemCount(totalQuantity);
    }, [totalQuantity, previousItemCount]);

    // Don't show if cart isn't initialized or has no items
    if (!isInitialized || items.length === 0) {
        return null;
    }

    // Get cart URL using helper
    const cartUrl = getCartUrl(locale);

    return (
        <div className="fixed right-4 bottom-20 z-50 font-neue-haas">
            <Link
                href={cartUrl}
                className={cn(
                    'relative flex items-center justify-center rounded-full bg-haug-lila-100 p-4 text-haug-ivory-100 shadow-lg',
                    'transition-all duration-200 hover:bg-haug-lila-200',
                    isAnimating && 'scale-110 animate-bounce'
                )}
            >
                {/* Shopping Cart Icon */}
                <ShoppingCart className="h-6 w-6" />

                {/* Quantity Badge */}
                {totalQuantity > 0 && (
                    <span
                        className={cn(
                            '-right-2 -top-2 absolute flex h-6 w-6 items-center justify-center rounded-full bg-haug-coral-100 font-bold text-haug-ivory-100 text-xs',
                            isAnimating && 'animate-pulse'
                        )}
                    >
                        {totalQuantity > 99 ? '99+' : totalQuantity}
                    </span>
                )}
            </Link>
        </div>
    );
}
